import type { CSSProperties } from 'react';

/**
 * A score band, in the colour the customer chose for it.
 *
 * <p>This is the one place a colour genuinely cannot come from a token: the bands are rows
 * in the customer's own parameters screen and they pick the colour. So it is passed inline,
 * which is what inline style is for.</p>
 *
 * <p>The text colour is worked out from the background rather than always being white. A
 * customer who picks pale amber for "iyi" would otherwise get white on #FBBF24 — about
 * 1.8:1, which is unreadable, and nothing in the product would have told them.</p>
 */
function readableOn(background: string): string {
  const hex = background.trim().replace('#', '');
  const full =
    hex.length === 3
      ? hex.split('').map((c) => c + c).join('')
      : hex;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) return 'var(--color-text)';

  const channel = (at: number) => {
    const value = parseInt(full.slice(at, at + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };

  // Relative luminance, per WCAG. Above the usual 0.179 crossover, black reads better.
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.179 ? 'var(--brand-navy)' : 'var(--brand-white)';
}

export function BandChip({ band, colour }: { band: string; colour: string | null }) {
  const style: CSSProperties = colour
    ? { background: colour, color: readableOn(colour), borderColor: 'transparent' }
    : {};

  return (
    <span className="nv-badge nv-badge--neutral" style={style}>
      {band}
    </span>
  );
}
